// Regulation Form Page (Create/Edit)
// Generated for Task 12.2 - Create regulations management frontend

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import RegulationsService from '../services/regulationsService';
import { CreateRegulationRequest, RegulationFormErrors } from '../types/regulation';

// Simple toast replacement (react-hot-toast not installed)
const toast = {
  error: (msg: string) => console.error('[Toast]', msg),
  success: (msg: string) => console.log('[Toast]', msg),
};

const RegulationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<Partial<CreateRegulationRequest>>({
    title: '',
    description: '',
    content: '',
    scope: { type: 'national', value: '' },
    category: '',
    effective_date: '',
    expiry_date: '',
    priority: 'medium',
    tags: [],
    affected_hotels: []
  });

  const [errors, setErrors] = useState<RegulationFormErrors>({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [_tags, _setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    loadCategories();
    loadTags();
    if (isEditMode) {
      loadRegulation();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const categoriesData = await RegulationsService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  const loadTags = async () => {
    try {
      const tagsData = await RegulationsService.getTags();
      _setTags(tagsData);
    } catch (err) {
      console.error('Erreur lors du chargement des tags:', err);
    }
  };

  const loadRegulation = async () => {
    try {
      setLoading(true);
      const response = await RegulationsService.getRegulation(Number(id));
      const reg = response.regulation;
      
      setFormData({
        title: reg.title,
        description: reg.description,
        content: reg.content,
        scope: reg.scope,
        category: reg.category,
        effective_date: reg.effective_date.split('T')[0],
        expiry_date: reg.expiry_date ? reg.expiry_date.split('T')[0] : '',
        priority: reg.priority,
        tags: reg.tags || [],
        affected_hotels: reg.affected_hotels || []
      });
    } catch (err: any) {
      toast.error('Erreur lors du chargement de la réglementation');
      navigate('/regulations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateRegulationRequest, value: any) => {
    setFormData((prev: Partial<CreateRegulationRequest>) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field as keyof RegulationFormErrors]) {
      setErrors((prev: RegulationFormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleScopeChange = (field: 'type' | 'value', value: string) => {
    setFormData((prev: Partial<CreateRegulationRequest>) => ({
      ...prev,
      scope: { ...prev.scope!, [field]: value }
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev: Partial<CreateRegulationRequest>) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev: Partial<CreateRegulationRequest>) => ({
      ...prev,
      tags: prev.tags?.filter((t: string) => t !== tag) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const validationErrors = RegulationsService.validateRegulation(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditMode) {
        await RegulationsService.updateRegulation(id!, {
          ...formData as CreateRegulationRequest,
          version: 1
        });
        toast.success('Réglementation mise à jour avec succès');
      } else {
        await RegulationsService.createRegulation(formData as CreateRegulationRequest);
        toast.success('Réglementation créée avec succès');
      }
      
      navigate('/regulations');
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/regulations"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Retour à la liste
          </Link>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Modifier la réglementation' : 'Nouvelle réglementation'}
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6 space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={`mt-1 block w-full border ${errors.title ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Titre de la réglementation"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className={`mt-1 block w-full border ${errors.description ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Description courte de la réglementation"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Contenu <span className="text-red-500">*</span>
              </label>
              <textarea
                id="content"
                rows={10}
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                className={`mt-1 block w-full border ${errors.content ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono`}
                placeholder="Contenu détaillé de la réglementation"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.content}
                </p>
              )}
            </div>

            {/* Category and Priority */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className={`mt-1 block w-full border ${errors.category ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="__new__">+ Nouvelle catégorie</option>
                </select>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.category}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                  Priorité <span className="text-red-500">*</span>
                </label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className={`mt-1 block w-full border ${errors.priority ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                >
                  <option value="low">Faible</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Élevée</option>
                  <option value="critical">Critique</option>
                </select>
                {errors.priority && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.priority}
                  </p>
                )}
              </div>
            </div>

            {/* Scope */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portée <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="scope-type" className="block text-xs text-gray-500 mb-1">
                    Type
                  </label>
                  <select
                    id="scope-type"
                    value={formData.scope?.type}
                    onChange={(e) => handleScopeChange('type', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="national">National</option>
                    <option value="province">Province</option>
                    <option value="city">Ville</option>
                    <option value="category">Catégorie d'hôtel</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="scope-value" className="block text-xs text-gray-500 mb-1">
                    Valeur
                  </label>
                  <input
                    type="text"
                    id="scope-value"
                    value={formData.scope?.value}
                    onChange={(e) => handleScopeChange('value', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={
                      formData.scope?.type === 'national' ? 'RDC' :
                      formData.scope?.type === 'province' ? 'Kinshasa' :
                      formData.scope?.type === 'city' ? 'Goma' :
                      '5 étoiles'
                    }
                  />
                </div>
              </div>
              {errors.scope && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.scope}
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="effective_date" className="block text-sm font-medium text-gray-700">
                  Date d'entrée en vigueur <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="effective_date"
                  value={formData.effective_date}
                  onChange={(e) => handleChange('effective_date', e.target.value)}
                  className={`mt-1 block w-full border ${errors.effective_date ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.effective_date && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.effective_date}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700">
                  Date d'expiration (optionnel)
                </label>
                <input
                  type="date"
                  id="expiry_date"
                  value={formData.expiry_date}
                  onChange={(e) => handleChange('expiry_date', e.target.value)}
                  className={`mt-1 block w-full border ${errors.expiry_date ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.expiry_date && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.expiry_date}
                  </p>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Ajouter un tag"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Ajouter
                </button>
              </div>
              
              {formData.tags && formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3">
            <Link
              to="/regulations"
              className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Enregistrement...' : isEditMode ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegulationFormPage;
